/**
 * Cross-category compatibility for the product-mix selects (see product-mix-products.liquid).
 *
 * Rules live on the component products themselves: `custom.incompatible_with`
 * (list.product_reference) on e.g. a mechanism product lists the seat products it cannot be
 * combined with, rendered onto each <option> as `data-mix-incompatible-with="<id>,<id>"`. The rule
 * map built from those attributes is symmetric — a pair maintained on one side also blocks the
 * reverse pairing — so merchants only ever maintain it once, on whichever product is more natural.
 *
 * Two effects, both recomputed from scratch on every change (no incremental state to get stale):
 * 1. Every option incompatible with what is currently selected in ANOTHER category is disabled and
 *    gets a "(not combinable)" suffix. Options the server rendered as disabled (out of stock) stay
 *    disabled regardless — their original state is remembered so re-enabling never resurrects them.
 * 2. The select the customer just touched wins: any other select left holding a now-incompatible
 *    product falls back to its placeholder, and a bubbling change event is dispatched for it so the
 *    price aggregator, the gallery and the required-selection gating all follow along — the empty
 *    value re-blocks add-to-cart via product-configuration-product.js until a new choice is made.
 *
 * Deliberately separate from product-mix-gallery.js: that one reflects selections, this one
 * constrains them, and they share nothing but the same selects.
 */

const SCOPE_SELECTOR = ".product__options-combined"
const MIX_SELECT_SELECTOR = "select[data-mix-cap]"

/** Guards against re-entering our own handler through the change events we dispatch ourselves. */
let syncing = false

/**
 * @param {HTMLSelectElement} select
 * @returns {string} product id of the current selection, "" for the placeholder
 */
function selectedProductId(select) {
  return select.options[select.selectedIndex]?.dataset.mixProductId || ""
}

/**
 * Product id -> ids it cannot be combined with, both directions.
 * @param {HTMLSelectElement[]} selects
 * @returns {Map<string, Set<string>>}
 */
function buildRuleMap(selects) {
  /** @type {Map<string, Set<string>>} */
  const rules = new Map()

  const add = (a, b) => {
    if (!a || !b || a === b) return
    if (!rules.has(a)) rules.set(a, new Set())
    rules.get(a)?.add(b)
  }

  for (const select of selects) {
    for (const option of select.options) {
      const productId = option.dataset.mixProductId
      if (!productId) continue

      for (const blockedId of (option.dataset.mixIncompatibleWith || "").split(",")) {
        const other = blockedId.trim()
        add(productId, other)
        add(other, productId)
      }
    }
  }

  return rules
}

/**
 * @param {Map<string, Set<string>>} rules
 * @param {string} productId
 * @param {string[]} againstIds
 */
function conflicts(rules, productId, againstIds) {
  if (!productId) return false
  const blocked = rules.get(productId)
  if (!blocked) return false

  return againstIds.some((id) => blocked.has(id))
}

/**
 * Applies the disabled state and the "(not combinable)" suffix to one option, keeping the
 * server-rendered out-of-stock state intact.
 * @param {HTMLOptionElement} option
 * @param {boolean} blocked
 * @param {string} suffix
 */
function markOption(option, blocked, suffix) {
  // First touch: remember how the server rendered this option, so both the disabled state and the
  // label (which already carries the stock hint) can be restored verbatim.
  if (!("mixStockDisabled" in option.dataset)) {
    option.dataset.mixStockDisabled = option.disabled ? "1" : "0"
    option.dataset.mixBaseLabel = option.textContent || ""
  }

  const outOfStock = option.dataset.mixStockDisabled === "1"
  const baseLabel = option.dataset.mixBaseLabel || ""

  option.disabled = outOfStock || blocked
  option.toggleAttribute("data-mix-incompatible", blocked)

  const label = blocked && suffix ? `${baseLabel} (${suffix})` : baseLabel
  if (option.textContent !== label) option.textContent = label
}

/**
 * @param {HTMLElement} scope - .product__options-combined
 * @param {HTMLSelectElement} [changedSelect] - the select the customer just touched, if any
 */
function apply(scope, changedSelect) {
  const selects = /** @type {HTMLSelectElement[]} */ (Array.from(scope.querySelectorAll(MIX_SELECT_SELECTOR)))
  if (selects.length < 2) return

  const rules = buildRuleMap(selects)
  if (!rules.size) return

  // Resolve first, mark second: the reset selections must not count towards what the remaining
  // options are evaluated against.
  const reset = []
  if (changedSelect) {
    const changedId = selectedProductId(changedSelect)
    for (const select of selects) {
      if (select === changedSelect) continue
      if (!conflicts(rules, selectedProductId(select), [changedId])) continue

      select.value = ""
      reset.push(select)
    }
  }

  for (const select of selects) {
    const otherIds = selects.filter((other) => other !== select).map(selectedProductId).filter(Boolean)
    const suffix = select.dataset.mixIncompatibleLabel || ""

    for (const option of select.options) {
      const productId = option.dataset.mixProductId
      if (!productId) continue

      markOption(option, conflicts(rules, productId, otherIds), suffix)
    }
  }

  // Let everything downstream (price aggregator, gallery, add-to-cart gating) see the reset.
  if (!reset.length) return
  syncing = true
  try {
    for (const select of reset) {
      select.dispatchEvent(new Event("change", { bubbles: true }))
    }
  } finally {
    syncing = false
  }
}

function initAll() {
  document.querySelectorAll(SCOPE_SELECTOR).forEach((scope) => {
    if (scope instanceof HTMLElement && scope.querySelector(MIX_SELECT_SELECTOR)) apply(scope)
  })
}

document.addEventListener("change", (event) => {
  if (syncing) return

  const target = event.target
  if (!(target instanceof HTMLSelectElement) || !target.matches(MIX_SELECT_SELECTOR)) return

  const scope = target.closest(SCOPE_SELECTOR)
  if (scope instanceof HTMLElement) apply(scope, target)
})

document.addEventListener("shopify:section:load", initAll)

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll)
} else {
  initAll()
}
